import { Dispatch, SetStateAction, useState } from 'react'
import cx from 'classnames'
import { Formik, Form, Field } from 'formik'
import { capitalize } from 'lodash'
import * as yup from 'yup'

import Styles from './SamlSsoLogin.module.scss'
import Shared from '../../Shared.module.scss'
import { ReactComponent as RightArrowIcon } from 'assets/icons/arrowRight24.svg'
import { reportError } from 'utils/error'

import { CustomDomainWarning } from '../CustomDomainWarning/CustomDomainWarning'
import { FormErrorMessage } from 'common/FormErrorMessage/FormErrorMessage'
import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { useLocation } from 'react-router-dom'
import RightPanelContent from '../RightPanelContent/RightPanelContent'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { GhostButton } from 'components/Buttons/GhostButton'
import { ReactComponent as LeftArrowIcon } from 'assets/icons/arrowLeft24.svg'
import { Button } from 'components/Button'
import { navigateToExternalUrl } from 'utils/location'
import { useIsBlaze } from 'hooks/useIsBlaze'

type Props = {
  transitionPage: (page: string) => void
  redirectTo: string
  email: string | ''
  setEmail: Dispatch<SetStateAction<string>>
}

const validationSchema = yup.object().shape({
  email: yup.string().email().required(),
})

const SsoLogin = ({ transitionPage, redirectTo, email, setEmail }: Props) => {
  const { isTabletPortrait } = useResponsiveDesign()
  const [loginError, setLoginError] = useState<string | undefined>(undefined)
  const [isProcessing, setIsProcessing] = useState(false)
  const [focused, setFocused] = useState(false)
  const location = useLocation()
  const isBlaze = useIsBlaze()

  const ssoLogin = async ({ email }: { email: string }) => {
    setIsProcessing(true)

    const res = await client.samlLogin(email, redirectTo)

    if (res.type !== SUCCESS_RESPONSE_TYPE || !res.data.redirect_url) {
      setIsProcessing(false)
      setLoginError(
        `${res.data.errors[0]?.title}. Please contact your administrator.` || 'Something went wrong. Please try again.',
      )
      reportError('Failed to login with SAML SSO', {
        severity: 'error',
        metaData: { email, res },
      })
      return
    }

    // Redirect to the SAML IdP
    navigateToExternalUrl(res.data.redirect_url)
  }

  const header = location.pathname === '/signup' ? 'Sign up with SAML SSO' : 'Log in with SAML SSO'

  const handleEmailFieldFocus = () => {
    setFocused(true)
  }

  return (
    <div className={cx(Shared.content, { [Shared.isBlaze]: isBlaze })}>
      <div className={Shared.leftPanel}>
        <div className={Shared.header}>
          <GhostButton
            className={cx(Shared.backButton, { [Shared.isBlaze]: isBlaze })}
            aria-label="Back"
            onClick={() => transitionPage('GO_BACK')}
          >
            <LeftArrowIcon />
          </GhostButton>
        </div>

        <div className={cx(Shared.largerLeftContent, Styles.largerLeftContent, { [Styles.isBlaze]: isBlaze })}>
          <div className={cx(Shared.title, Styles.title, { [Shared.isBlaze]: isBlaze })}>{header}</div>
          <CustomDomainWarning />
          <Formik initialValues={{ email }} onSubmit={ssoLogin} validationSchema={validationSchema}>
            {({ errors, touched }) => (
              <Form className={Shared.form}>
                <div className={Shared.inputField}>
                  <label htmlFor="email">Email Address</label>
                  <div
                    className={cx(Shared.inlineInputWrapper, {
                      [Shared.focused]: focused && !(touched.email && errors.email),
                      [Shared.inputErrors]: touched.email && errors.email,
                    })}
                    onClick={handleEmailFieldFocus}
                  >
                    <Field
                      id="email"
                      name="email"
                      placeholder="Enter your email"
                      data-test-id="saml-sso-email"
                      onBlur={(e: any) => setEmail(e.currentTarget.value)}
                      onFocus={() => setFocused(true)}
                      disabled={isProcessing}
                      autoFocus
                    />
                    {focused && !isTabletPortrait && (
                      <div className={Shared.inlineButtonContainer}>
                        <button type="submit" className={Shared.inlineSubmitButton} aria-label="Submit">
                          <RightArrowIcon />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                {touched.email && errors.email && <FormErrorMessage>{capitalize(errors.email)}</FormErrorMessage>}
                {loginError && (
                  <FormErrorMessage className={Styles.errorWrap}>
                    <div className={Styles.error}>{loginError}</div>
                  </FormErrorMessage>
                )}
                {isTabletPortrait && (
                  <Button type="submit" className={Shared.actionButton} isDisabled={isProcessing}>
                    <span>Continue</span>
                    <span className={Shared.rightArrow}>&#8594;</span>{' '}
                  </Button>
                )}
              </Form>
            )}
          </Formik>
        </div>
      </div>
      {!isTabletPortrait && <RightPanelContent screen="usingSamlSso" />}
    </div>
  )
}

export default SsoLogin
